import { compact } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { reactive, toRefs } from "vue";

import { Cancellation } from "@/api";
import { useDebounce, useSingleton } from "@/composables";
import { EntitiesHelper, SearchFilterHelper } from "@/helpers";
import { SuggestionsService } from "@/services";
import type { Suggestion, SuggestionSelection } from "@/types";

import { useDictionariesStore } from "./dictionaries.store";
import { useSearchFilterStore } from "./search-filter.store";

interface State {
  status: "initial" | "loading" | "loaded" | "failed";
  suggestions: Suggestion[];
}

const STORE_ID = "suggestions";
const SUGGESTION_SELECTION_STORE_KEY = `${STORE_ID}.queued_selection`;

const getSuggestionsSingleton = useSingleton(SuggestionsService.getSuggestions);

export const useSuggestionsStore = defineStore(STORE_ID, () => {
  const state = reactive<State>({
    status: "initial",
    suggestions: []
  });

  const dictionariesStore = useDictionariesStore();
  const searchFilterStore = useSearchFilterStore();

  const getSuggestions = useDebounce(async (query: string, methodIds: string[]) => {
    if (query.length < 3) {
      state.suggestions = [];
      return;
    }

    const prevStatus = state.status;

    const methods = compact(methodIds.map((methodId) => EntitiesHelper.findById(dictionariesStore.methods, methodId)));
    const filteredMethods = SearchFilterHelper.filterMethods(
      dictionariesStore.dictionaries,
      searchFilterStore.filter,
      methods
    );

    try {
      state.status = "loading";
      state.suggestions = await getSuggestionsSingleton(
        query,
        filteredMethods.map((method) => method.id)
      );
      state.status = "loaded";
    } catch (e) {
      if (e instanceof Cancellation) {
        state.status = prevStatus;
        return;
      }

      state.status = "failed";
      throw e;
    }
  }, 180);

  const clearSuggestions = () => {
    state.suggestions = [];
  };

  const storeSuggestionSelection = (selection: SuggestionSelection) => {
    localStorage.setItem(SUGGESTION_SELECTION_STORE_KEY, JSON.stringify(selection));
  };

  const logStoredSuggestionSelection = () => {
    const localStorageItem = localStorage.getItem(SUGGESTION_SELECTION_STORE_KEY);
    if (localStorageItem === null) {
      return;
    }

    const suggestion = JSON.parse(localStorageItem) as SuggestionSelection;

    SuggestionsService.logSuggestion(
      suggestion.uuid,
      suggestion.selectedSuggestion,
      suggestion.index,
      suggestion.engine
    );

    localStorage.removeItem(SUGGESTION_SELECTION_STORE_KEY);
  };

  return {
    ...toRefs(state),
    clearSuggestions,
    getSuggestions,
    storeSuggestionSelection,
    logStoredSuggestionSelection
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useSuggestionsStore, import.meta.hot));
}
